import { useState, useMemo } from "react";
import { useTranslations, cx, useAppVariant } from "@jugl-web/utils";
import { Highlightify } from "@jugl-web/utils/utils/Highlightify";
import { Linkify } from "@jugl-web/utils/utils/Linkify";
import {
  Avatar,
  Collapse,
  PlainButton,
  Checkbox,
} from "@jugl-web/ui-components/cross-platform";
import { Tooltip } from "@jugl-web/ui-components";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";
import { ReactComponent as CheckIcon } from "./assets/check.svg";

export const UserCardItem = ({
  variant,
  id,
  name,
  subtitle,
  attributes,
  isSelected,
  searchQuery,
  avatarUrl,
  onSelect,
  isExpandButtonVisible = true,
  selectVariant = "default",
}: {
  variant: "customer" | "user";
  id: string;
  name: string;
  subtitle: string;
  attributes: {
    id: string;
    label: string;
    value: string | null;
    isHidden?: boolean;
    preventSearchQueryHighlight?: boolean;
  }[];
  isSelected?: boolean;
  searchQuery?: string;
  avatarUrl?: string;
  onSelect: (customerId: string) => void;
  isExpandButtonVisible?: boolean;
  selectVariant?: "checkbox" | "default";
}) => {
  const { t } = useTranslations();
  const { isMobile } = useAppVariant();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const shouldHighlightExpandButton = useMemo(() => {
    if (!searchQuery) {
      return false;
    }
    return attributes.some(
      (attr) =>
        !attr?.preventSearchQueryHighlight &&
        !attr?.isHidden &&
        attr &&
        attr.value?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [attributes, searchQuery]);

  const selectUserTooltip =
    variant === "user"
      ? t({
          id: "customers-page.select-user",
          defaultMessage: "Select User",
        })
      : t({
          id: "customers-page.select-customer",
          defaultMessage: "Select Customer",
        });
  const userSelectedTooltip =
    variant === "user"
      ? t({
          id: "customers-page.user-selected",
          defaultMessage: "User Selected",
        })
      : t({
          id: "customers-page.customer-selected",
          defaultMessage: "Customer Selected",
        });
  const selectTooltip = isSelected ? userSelectedTooltip : selectUserTooltip;
  const visibleAttributes = useMemo(
    () => attributes.filter((attr) => !attr?.isHidden),
    [attributes]
  );
  return (
    <div
      className={cx("rounded-[14px] p-3 font-secondary", {
        "cursor-pointer": variant === "customer",
        "hover:bg-grey-100": !isMobile,
        "bg-grey-100": isExpanded,
        "bg-[#EFF8FF] hover:bg-[#EAF6FF]": isSelected && !isExpanded,
      })}
      onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
      onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}
      onClick={() => {
        onSelect(id);
      }}
    >
      <div
        className={cx("z-10 flex h-[46px] items-center justify-between", {
          "h-[46px]": !isMobile,
          "h-[38px]": isMobile,
        })}
      >
        <div className="flex items-center gap-3">
          {isMobile && (
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  className={cx(
                    "pointer-events-auto h-8 w-8 rounded-lg hover:bg-grey-200",
                    {
                      "bg-primary/10": shouldHighlightExpandButton,
                      "bg-grey-200 hover:bg-grey-300": isExpanded,
                    }
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }}
                >
                  <ChevronDownIcon
                    className={cx("rounded-lg", {
                      "fill-primary": shouldHighlightExpandButton,
                      "rotate-180 transition-all": isExpanded,
                    })}
                  />
                </PlainButton>
              )}
            >
              {isExpanded
                ? t({
                    id: "customers-page.collapse-details",
                    defaultMessage: "Close Details Info",
                  })
                : t({
                    id: "customers-page.expand-details",
                    defaultMessage: "See Details Info",
                  })}
            </Tooltip>
          )}
          <div className="flex rounded-full border-[1px] border-solid border-[#E0E0E0] bg-white p-0.5">
            <Avatar
              imageUrl={avatarUrl}
              username={name}
              className="h-[40px] w-[40px]"
            />
          </div>
          <div>
            <div
              className={cx("max-w-[250px] truncate text-dark", {
                "w-[200px]": isMobile,
              })}
              title={name}
            >
              <Highlightify
                highlightClassName="text-primary"
                searchWord={searchQuery || ""}
              >
                {name}
              </Highlightify>
            </div>
            <div
              className={cx("max-w-[250px] truncate text-sm text-[#828282]", {
                "w-[200px]": isMobile,
              })}
              title={subtitle}
            >
              {subtitle}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {(isExpanded || isHovered || isSelected) &&
            selectVariant === "default" && (
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    ref={ref}
                    {...props}
                    className={cx(
                      "pointer-events-auto h-8 w-8 rounded-lg fill-grey-background",
                      {
                        "hover:bg-grey-200": !isMobile,
                        "bg-grey-200 hover:bg-grey-300":
                          !isMobile && isExpanded,
                        "fill-primary": isSelected,
                        "bg-primary/10": !isMobile && isSelected && isExpanded,
                      }
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(id);
                    }}
                  >
                    <CheckIcon />
                  </PlainButton>
                )}
              >
                {selectTooltip}
              </Tooltip>
            )}
          {!isMobile && isExpandButtonVisible && (
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  className={cx(
                    "pointer-events-auto h-[32px] w-[32px] rounded-lg hover:bg-grey-200",
                    {
                      "bg-primary/10": shouldHighlightExpandButton,
                      "bg-grey-200 hover:bg-grey-300": isExpanded,
                    }
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }}
                >
                  <ChevronDownIcon
                    className={cx("rounded-lg", {
                      "fill-primary": shouldHighlightExpandButton,
                      "rotate-180 transition-all": isExpanded,
                    })}
                  />
                </PlainButton>
              )}
            >
              {isExpanded
                ? t({
                    id: "customers-page.collapse-details",
                    defaultMessage: "Close Details Info",
                  })
                : t({
                    id: "customers-page.expand-details",
                    defaultMessage: "See Details Info",
                  })}
            </Tooltip>
          )}
          {selectVariant === "checkbox" && (
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  className={cx(
                    "pointer-events-auto h-8 w-8 rounded-lg fill-grey-background"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(id);
                  }}
                >
                  <Checkbox readOnly isChecked={isSelected} />
                </PlainButton>
              )}
            >
              {selectTooltip}
            </Tooltip>
          )}
        </div>
      </div>

      <Collapse isOpen={isExpanded}>
        <div
          className={cx(
            "overflow-hidden rounded-b-xl px-4 pt-3 font-secondary",

            {
              "rounded-tl-none rounded-tr-none": isExpanded,
              hidden: !isExpanded,
            }
          )}
        >
          <div className="h-[1px] w-full bg-[#EAECEE]" />
          <div className="flex flex-col gap-4 pt-4">
            {visibleAttributes.length === 0 && (
              <div className="text-sm text-grey">
                {t({
                  id: "customers-page.no-additional-info",
                  defaultMessage: "No additional info available",
                })}
              </div>
            )}
            {visibleAttributes.map(
              (attr) =>
                attr && (
                  <div
                    key={attr.id}
                    className="flex flex-col gap-0.5 last:pb-0"
                  >
                    <div className="text-xs text-dark-600 capitalize">
                      {attr.label}
                    </div>
                    <div className="text-sm break-words text-dark">
                      {!attr?.preventSearchQueryHighlight ? (
                        <Highlightify
                          highlightClassName="text-primary"
                          searchWord={searchQuery || ""}
                        >
                          <Linkify className="text-primary-800">
                            {attr.value}
                          </Linkify>
                        </Highlightify>
                      ) : (
                        <Linkify className="text-primary-800">
                          {attr.value}
                        </Linkify>
                      )}
                    </div>
                  </div>
                )
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};
